/* eslint-disable no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { FaSpinner, FaRobot } from "react-icons/fa";
import axios from "axios";
import { BASEURL } from "../../utilites";
import Offcanvas from "../../components/Dashboard/Offcanvas";
import { BsFillPersonFill } from "react-icons/bs";
import VoiceWavesLottie from "./VoiceWavesLottie";

const AiVoiceSupport = ({ isMobile, handleSubmit }) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("idle");
  const [mode, setMode] = useState("idle");
  const [lastText, setLastText] = useState("");
  const [isPaused, setIsPaused] = useState(false);
  const [visualMode, setVisualMode] = useState("idle");

  const offcanvasElId = "voiceAssistantSidebar";
  const OffcanvasCtorRef = useRef(null);
  const offcanvasInstRef = useRef(null);

  const pcRef = useRef(null);
  const dataChannelRef = useRef(null);
  const micStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);

  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const audioRafRef = useRef(null);

  // Track function calls from Realtime (call_id -> { name })
  const functionCallsRef = useRef({});

  const ensureCtor = async () => {
    if (OffcanvasCtorRef.current) return OffcanvasCtorRef.current;
    if (typeof window === "undefined") return null;
    const mod = await import("bootstrap/js/dist/offcanvas");
    OffcanvasCtorRef.current = mod.default || mod.Offcanvas;
    return OffcanvasCtorRef.current;
  };

  const ensureOffcanvas = async () => {
    if (offcanvasInstRef.current) return offcanvasInstRef.current;
    const Ctor = await ensureCtor();
    const el = document.getElementById(offcanvasElId);
    if (!Ctor || !el) return null;
    offcanvasInstRef.current = Ctor.getOrCreateInstance(el, {
      backdrop: true,
      scroll: true,
    });
    return offcanvasInstRef.current;
  };

  const openSidebar = async () => {
    const inst = await ensureOffcanvas();
    if (!inst) return;
    const el = inst._element;
    if (!el.classList.contains("show")) inst.show();
  };

  const closeSidebar = async () => {
    const inst = await ensureOffcanvas();
    if (!inst) return;
    const el = inst._element;
    if (el?.classList?.contains("show")) inst.hide();
  };

  const startAnalyserLoop = () => {
    if (!analyserRef.current) return;
    const analyser = analyserRef.current;
    const bufferLength = analyser.fftSize || 512;
    const dataArray = new Uint8Array(bufferLength);

    const loop = () => {
      const a = analyserRef.current;
      if (!a) return;

      a.getByteTimeDomainData(dataArray);
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const v = (dataArray[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / dataArray.length);

      const nextMode = rms > 0.02 ? "speaking" : "listening";

      setVisualMode((prev) => (prev === nextMode ? prev : nextMode));

      audioRafRef.current = requestAnimationFrame(loop);
    };

    if (audioRafRef.current) cancelAnimationFrame(audioRafRef.current);
    audioRafRef.current = requestAnimationFrame(loop);
  };

  const setupAnalyser = (remoteStream) => {
    if (analyserRef.current) return;
    if (typeof window === "undefined") return;

    const AudioCtx =
      window.AudioContext || window.webkitAudioContext || null;
    if (!AudioCtx) return;

    const ctx = new AudioCtx();
    audioContextRef.current = ctx;

    const source = ctx.createMediaStreamSource(remoteStream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.85;

    source.connect(analyser);
    analyserRef.current = analyser;

    ctx.resume().catch(() => {});
    startAnalyserLoop();
  };

  const createPeerConnection = () => {
    if (pcRef.current) return pcRef.current;

    const pc = new RTCPeerConnection({ iceServers: [] });
    pcRef.current = pc;

    pc.oniceconnectionstatechange = () => {
      const s = pc.iceConnectionState;
      if (s === "connected") {
        setStatus("connected");
        setMode("listening");
      } else if (s === "failed" || s === "disconnected") {
        setStatus("disconnected");
        setMode("idle");
        setVisualMode("idle");
      }
    };

    pc.ontrack = (event) => {
      try {
        const [remoteStream] = event.streams;
        if (!remoteAudioRef.current) return;
        remoteAudioRef.current.srcObject = remoteStream;
        setupAnalyser(remoteStream);
      } catch (e) {
        console.error("ontrack error:", e);
      }
    };

    return pc;
  };

  const sendEvent = (payload) => {
    const dc = dataChannelRef.current;
    if (!dc || dc.readyState !== "open") return;
    dc.send(JSON.stringify(payload));
  };

  // ====================== TOOL IMPLEMENTATIONS ======================
  const sendToolResultToRealtime = (callId, output) => {
    const dc = dataChannelRef.current;
    if (!dc || dc.readyState !== "open") return;

    const toolEvent = {
      type: "conversation.item.create",
      item: {
        type: "function_call_output",
        call_id: callId,
        output: JSON.stringify(output || {}),
      },
    };

    dc.send(JSON.stringify(toolEvent));

    const responseEvent = { type: "response.create" };
    dc.send(JSON.stringify(responseEvent));
  };

const handleFunctionCall = async (callId, toolName, argsJson) => {
  let args = {};
  try {
    args = argsJson ? JSON.parse(argsJson) : {};
  } catch (e) {
    console.error("Failed to parse tool args:", e, argsJson);
  }

if (toolName === "send_chat_message") {
  const text = (args?.text || "").trim();
  const attachments = Array.isArray(args?.attachments)
    ? args.attachments
    : [];

  const parts = [];

  // Main text first
  if (text) {
    parts.push(text);
  }

  // Attachments as markdown bullet list with links
  if (attachments.length) {
    const lines = attachments
      .map((att, idx) => {
        const url = att?.url?.trim();
        if (!url) return null;

        const label =
          (att?.title && att.title.trim()) ||
          (att?.type === "document"
            ? `Document ${idx + 1}`
            : url);

        // Markdown: bullet + clickable link
        return `- [${label}](${url})`;
      })
      .filter(Boolean);

    if (lines.length) {
      // blank line before list so markdown treats it as a list
      parts.push("", ...lines);
    }
  }

  const body = parts.join("\n");

  if (!body.trim()) {
    // optional: fail fast if totally empty
    // handleSubmit({ type: "ai/Response", encodedString: "(empty message from AI)" });
    return;
  }

  handleSubmit({
    type: "ai/Response",
    encodedString: body,
  });

  return;
}


  try {
    const resp = await axios.post(
      `${BASEURL}/api/voiceassistant/ai/tool-call`,
      {
        toolName,
        args,
      },
      { withCredentials: true }
    );

    const payload = resp?.data?.data || resp?.data;
    const output = payload?.output ?? payload;

    sendToolResultToRealtime(callId, output);
  } catch (err) {
    console.error("handleFunctionCall backend error:", err);

    sendToolResultToRealtime(callId, {
      ok: false,
      error:
        err?.response?.data?.message ||
        err.message ||
        "Backend tool call failed",
    });
  }
};



  // ====================== DATA CHANNEL ======================

  const createDataChannel = (pc) => {
    if (dataChannelRef.current) return dataChannelRef.current;

    const dc = pc.createDataChannel("oai-events");
    dataChannelRef.current = dc;

    dc.onopen = () => {
      // 1) Configure session (turn detection, etc.)
      const sessionUpdate = {
        type: "session.update",
        session: {
          turn_detection: {
            type: "server_vad",
          },
        },
      };
      dc.send(JSON.stringify(sessionUpdate));

      // 2) Create a "virtual" user message that tells the model to start with a greeting
      const greetingPrompt = {
        type: "conversation.item.create",
        item: {
          type: "message",
          role: "user",
          content: [
            {
              type: "input_text",
              text: "Start the conversation by greeting the user based on your instructions.",
            },
          ],
        },
      };
      dc.send(JSON.stringify(greetingPrompt));

      // 3) Ask the model to respond now
      const responseCreate = { type: "response.create" };
      dc.send(JSON.stringify(responseCreate));
    };

dc.onmessage = (event) => {
  let msg;
  try {
    msg = JSON.parse(event.data);
  } catch (e) {
    console.error("[RT] Failed to parse event data:", event.data);
    return;
  }

  const type = msg.type;
  console.log("[RT] event:", type, msg);

  // --- function_call tracking (new code) ---

  if (
    type === "response.output_item.added" &&
    msg.item?.type === "function_call"
  ) {
    const { call_id, name, arguments: argsJson } = msg.item || {};
    console.log("[RT] function_call requested:", { type, call_id, name, argsJson });
    if (call_id && name) {
      functionCallsRef.current[call_id] = { name };
    }
    return;
  }

  if (type === "response.function_call_arguments.done") {
    const { call_id, arguments: argsJson, name } = msg;
    const meta = functionCallsRef.current[call_id];
    const toolName = meta?.name || name;

    console.log("[RT] function_call_arguments.done:", {
      call_id,
      toolName,
      argsJson,
    });

    if (!toolName) {
      console.warn(
        "[RT] function_call_arguments.done for unknown call_id/name",
        { call_id, msg }
      );
      return;
    }

    handleFunctionCall(call_id, toolName, argsJson);
    return;
  }

  // Log server errors from OpenAI
  if (type === "error") {
    console.error("[RT] Realtime error from server:", msg);
    return;
  }

  // ===== existing transcript handling =====
  if (type === "response.created") {
    setLastText("");
    return;
  }

  if (
    type === "response.output_audio_transcript.delta" ||
    type === "response.audio_transcript.delta" ||
    type === "response.output_text.delta"
  ) {
    const delta =
      msg.delta?.text ||
      msg.delta?.transcript ||
      msg.delta ||
      msg.text ||
      "";
    if (delta) {
      setLastText((prev) => (prev + " " + delta).trim());
    }
    return;
  }

  // You can keep other branches here as needed
};


    dc.onerror = (err) => {
      console.error("DataChannel error:", err);
    };

    return dc;
  };

  const waitForIceGatheringComplete = (pc) =>
    new Promise((resolve) => {
      if (pc.iceGatheringState === "complete") {
        resolve();
        return;
      }
      const checkState = () => {
        if (pc.iceGatheringState === "complete") {
          pc.removeEventListener("icegatheringstatechange", checkState);
          resolve();
        }
      };
      pc.addEventListener("icegatheringstatechange", checkState);
    });

  const cleanup = async () => {
    try {
      if (dataChannelRef.current) {
        try {
          dataChannelRef.current.close();
        } catch {}
        dataChannelRef.current = null;
      }

      if (pcRef.current) {
        try {
          pcRef.current.getSenders().forEach((s) => {
            try {
              s.track && s.track.stop();
            } catch {}
          });
        } catch {}
        try {
          pcRef.current.close();
        } catch {}
        pcRef.current = null;
      }

      if (micStreamRef.current) {
        try {
          micStreamRef.current.getTracks().forEach((t) => t.stop());
        } catch {}
        micStreamRef.current = null;
      }

      if (remoteAudioRef.current) {
        try {
          remoteAudioRef.current.srcObject = null;
          remoteAudioRef.current.pause();
        } catch {}
      }

      if (audioRafRef.current) {
        cancelAnimationFrame(audioRafRef.current);
        audioRafRef.current = null;
      }

      if (audioContextRef.current) {
        try {
          audioContextRef.current.close();
        } catch {}
        audioContextRef.current = null;
      }

      analyserRef.current = null;
      functionCallsRef.current = {};
    } catch {}

    setStatus("disconnected");
    setMode("idle");
    setIsPaused(false);
    setLastText("");
    setVisualMode("idle");
  };

  const start = async () => {
    try {
      setLoading(true);
      setStatus("connecting");
      setMode("idle");
      setIsPaused(false);
      setLastText("");
      setVisualMode("idle");

      const { data } = await axios.get(
        `${BASEURL}/api/voiceassistant/ai/convai/session`,
        {
          params: { conn: "webrtc" },
          withCredentials: true,
          headers: { Accept: "application/json" },
        }
      );

      if (!data?.ok || !data?.token) {
        throw new Error(data?.message || "No token from backend");
      }

      const ephemeralKey = data.token;

      const pc = createPeerConnection();

      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      micStreamRef.current = micStream;
      micStream.getTracks().forEach((track) => pc.addTrack(track, micStream));

      createDataChannel(pc);

      await pc.setLocalDescription(await pc.createOffer());
      await waitForIceGatheringComplete(pc);

      const offerSdp = pc.localDescription.sdp;

      const realtimeUrl =
        "https://api.openai.com/v1/realtime/calls?model=gpt-realtime";

      const sdpResponse = await fetch(realtimeUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${ephemeralKey}`,
          "Content-Type": "application/sdp",
        },
        body: offerSdp,
      });

      if (!sdpResponse.ok) {
        console.error("SDP error:", sdpResponse.status, sdpResponse.statusText);
        throw new Error("Failed to start Realtime call");
      }

      const answerSdp = await sdpResponse.text();
      await pc.setRemoteDescription({ type: "answer", sdp: answerSdp });

      if (remoteAudioRef.current) {
        remoteAudioRef.current.muted = false;
      }

      setStatus("connected");
      setMode("listening");
      setIsPaused(false);

      await openSidebar();
    } catch (err) {
      console.error("Error starting OpenAI Realtime session:", err);
      await cleanup();
    } finally {
      setLoading(false);
    }
  };

  const stop = async () => {
    await cleanup();
    await closeSidebar();
  };

  const togglePause = () => {
    if (status !== "connected") return;

    const mic = micStreamRef.current;
    const audioEl = remoteAudioRef.current;

    if (!isPaused) {
      sendEvent({ type: "response.cancel" });
      sendEvent({
        type: "session.update",
        session: {
          turn_detection: null,
        },
      });

      if (mic) {
        mic.getAudioTracks().forEach((t) => {
          t.enabled = false;
        });
      }

      if (audioEl) {
        audioEl.muted = true;
      }

      setIsPaused(true);
      setMode("idle");
      setVisualMode("idle");
    } else {
      sendEvent({
        type: "session.update",
        session: {
          turn_detection: {
            type: "server_vad",
          },
        },
      });

      if (mic) {
        mic.getAudioTracks().forEach((t) => {
          t.enabled = true;
        });
      }

      if (audioEl) {
        audioEl.muted = false;
      }

      setIsPaused(false);
      setMode("listening");
    }
  };

  const handleClick = async (e) => {
    e.preventDefault();
    if (status === "connected") {
      await openSidebar();
      return;
    }
    return start();
  };

  useEffect(() => {
    let el;
    let hideHandler;

    (async () => {
      const inst = await ensureOffcanvas();
      el = inst?._element;
      if (!el) return;

      hideHandler = () => {
        cleanup();
      };

      el.addEventListener("hide.bs.offcanvas", hideHandler);
    })();

    return () => {
      if (el && hideHandler) {
        el.removeEventListener("hide.bs.offcanvas", hideHandler);
      }
      try {
        offcanvasInstRef.current?.dispose?.();
      } catch {}
      cleanup();
    };
  }, []);

  const isActive = status === "connected";
  const speaking = isActive && mode === "speaking" && !isPaused;

  return (
    <>
      <button
        type="button"
        className={`btn ${
          isActive ? "btn-success" : "btn-primary"
        } rounded-3 text-white px-3 px-sm-2 py-2 ml-md-2 ml-1 btn-sm d-flex align-items-center`}
        onClick={handleClick}
        disabled={loading}
        title="Talk to UNK AI"
      >
        {!loading && (
          <div>{isMobile ? <FaRobot /> : <span>UNK Visual Assistant</span>}</div>
        )}
        {speaking && (
          <div className="unk-eq ms-1" aria-hidden="true">
            <span />
            <span />
            <span />
            <span />
            <span />
          </div>
        )}

        {loading ? <FaSpinner className="unk-spin" /> : <span>{""}</span>}
      </button>

      <Offcanvas
        header={"UNK Visual Assistant"}
        position="end"
        trigger={offcanvasElId}
        style={{ width: 640 }}
      >
        <div className="p-4 d-flex flex-column align-items-center text-center w-100">
          <BsFillPersonFill
            style={{ fontSize: "120px" }}
            className="text-muted"
          />

          {lastText && (
            <div className="mt-4 fs-6 fs-md-5 text-muted px-5">{lastText}</div>
          )}

          <audio
            ref={remoteAudioRef}
            autoPlay
            playsInline
            style={{ display: "none" }}
          />
          <VoiceWavesLottie visualMode={isPaused ? "idle" : visualMode} />
          <div className="mt-4 fs-5 text-muted">
            {loading
              ? "Connecting…"
              : isActive
              ? isPaused
                ? "Paused"
                : "Listening…"
              : "Stopped"}
          </div>

          <div className="d-flex flex-wrap justify-content-center gap-2 mt-4">
            {isActive && (
              <button
                type="button"
                className="btn btn-outline-primary px-4 py-2"
                onClick={togglePause}
                disabled={loading}
              >
                {isPaused ? "Resume" : "Pause"}
              </button>
            )}

            {isActive && (
              <button
                type="button"
                className="btn btn-danger px-4 py-2"
                onClick={stop}
                disabled={loading}
              >
                End Conversation
              </button>
            )}
          </div>
        </div>
      </Offcanvas>
    </>
  );
};

export default AiVoiceSupport;
